import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Volume2, Play, Pause, Square, SkipBack, SkipForward, X } from "lucide-react";
import { useTTS } from "@/context/TTSContext";
import { T } from "@/context/LanguageContext";

const SPEEDS = [0.75, 1, 1.25, 1.5, 2];

export function FloatingAudioBar() {
  const tts = useTTS();
  if (!tts || !tts.supported) return null;
  const visible = tts.status !== "idle";

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, y: 60, x: "-50%" }}
          animate={{ opacity: 1, y: 0, x: "-50%" }}
          exit={{ opacity: 0, y: 60, x: "-50%" }}
          transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
          className="fixed bottom-5 left-1/2 z-[80] flex max-w-[calc(100vw-1.5rem)] items-center gap-2 rounded-full bg-navy px-3 py-2 text-white shadow-2xl ring-1 ring-white/10 sm:gap-3 sm:px-4"
          data-testid="floating-audio-bar"
        >
          <span className="flex items-center gap-2 pl-1 pr-1">
            <motion.span animate={{ scale: tts.status === "playing" ? [1, 1.25, 1] : 1 }} transition={{ repeat: Infinity, duration: 1.1 }}>
              <Volume2 className="h-4 w-4 text-oldglory-red" />
            </motion.span>
            <span className="hidden text-xs font-semibold sm:inline">
              <T>{tts.status === "playing" ? "Now playing" : "Paused"}</T>
            </span>
          </span>

          <span className="h-6 w-px bg-white/15" />

          <button onClick={tts.prev} className="flex h-8 w-8 items-center justify-center rounded-full text-white/80 transition-colors hover:bg-white/10 hover:text-white" data-testid="floating-audio-prev" aria-label="Previous paragraph">
            <SkipBack className="h-4 w-4" />
          </button>
          {tts.status === "playing" ? (
            <button onClick={tts.pause} className="flex h-10 w-10 items-center justify-center rounded-full bg-white text-navy transition-transform hover:scale-105" data-testid="floating-audio-pause" aria-label="Pause">
              <Pause className="h-5 w-5" />
            </button>
          ) : (
            <button onClick={tts.play} className="flex h-10 w-10 items-center justify-center rounded-full bg-white text-navy transition-transform hover:scale-105" data-testid="floating-audio-play" aria-label="Play">
              <Play className="h-5 w-5" />
            </button>
          )}
          <button onClick={tts.next} className="flex h-8 w-8 items-center justify-center rounded-full text-white/80 transition-colors hover:bg-white/10 hover:text-white" data-testid="floating-audio-next" aria-label="Next paragraph">
            <SkipForward className="h-4 w-4" />
          </button>

          <span className="h-6 w-px bg-white/15" />

          <div className="hidden items-center gap-1 md:flex">
            {SPEEDS.map((s) => (
              <button
                key={s}
                onClick={() => tts.setRate(s)}
                className={`rounded px-1.5 py-0.5 text-[11px] font-medium transition-colors ${tts.rate === s ? "bg-oldglory-red text-white" : "text-white/70 hover:bg-white/10"}`}
                data-testid={`floating-audio-speed-${s}`}
              >
                {s}×
              </button>
            ))}
            <span className="h-6 w-px bg-white/15" />
          </div>

          <button onClick={tts.stop} className="flex h-8 items-center gap-1.5 rounded-full bg-white/10 px-3 text-xs font-semibold text-white transition-colors hover:bg-oldglory-red" data-testid="floating-audio-close" aria-label="Stop and close">
            <X className="h-4 w-4" /> <span className="hidden sm:inline"><T>Close</T></span>
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
